
import { useState, useEffect } from 'react';
import {
  getNotificationsForClient,
  markAsRead,
  markAllAsRead,
  type Notification,
} from '../../../services/notifications';

interface NotificationPanelProps {
  clientId: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function NotificationPanel({
  clientId,
  isOpen,
  onClose,
}: NotificationPanelProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // ---------------------------------------------------------------------------
  // Load notifications whenever the panel is opened or the client changes.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, clientId]);

  /** Load notifications for the current client and update state safely. */
  const loadNotifications = () => {
    try {
      const clientNotifications = getNotificationsForClient(clientId);
      setNotifications(clientNotifications);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      setNotifications([]);
    }
  };

  /** Mark a single notification as read and refresh the list. */
  const handleMarkAsRead = (notificationId: string) => {
    try {
      markAsRead(notificationId);
      loadNotifications();
    } catch (error) {
      console.error(`Failed to mark notification ${notificationId} as read:`, error);
    }
  };

  /** Mark all notifications for the client as read and refresh the list. */
  const handleMarkAllAsRead = () => {
    try {
      markAllAsRead(clientId);
      loadNotifications();
    } catch (error) {
      console.error(`Failed to mark all notifications as read for client ${clientId}:`, error);
    }
  };

  /** Convert an ISO date string into a human‑readable relative time. */
  const getRelativeTime = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60)
      return (
        diffMins +
        ' ' +
        (diffMins === 1 ? 'minute' : 'minutes') +
        ' ago'
      );
    if (diffHours < 24)
      return (
        diffHours +
        ' ' +
        (diffHours === 1 ? 'hour' : 'hours') +
        ' ago'
      );
    if (diffDays < 7)
      return (
        diffDays +
        ' ' +
        (diffDays === 1 ? 'day' : 'days') +
        ' ago'
      );
    return date.toLocaleDateString();
  };

  /** Return the appropriate icon configuration for a given notification type. */
  const getNotificationIcon = (type: Notification['type']) => {
    const iconMap = {
      file_uploaded: {
        icon: 'ri-file-upload-line',
        color: 'text-teal-600',
        bg: 'bg-teal-50',
      },
      task_status_changed: {
        icon: 'ri-task-line',
        color: 'text-blue-600',
        bg: 'bg-blue-50',
      },
      project_status_changed: {
        icon: 'ri-folder-settings-line',
        color: 'text-purple-600',
        bg: 'bg-purple-50',
      },
    };
    return iconMap[type];
  };

  // ---------------------------------------------------------------------------
  // If the panel is closed, render nothing.
  // ---------------------------------------------------------------------------
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose}></div>

      {/* Slide‑in Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
              <i className="ri-notification-3-line text-xl text-teal-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Notifications</h2>
              <p className="text-xs text-gray-500">{notifications.length} total</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Actions */}
        {notifications.length > 0 && notifications.some((n) => !n.read) && (
          <div className="p-4 border-b border-gray-100">
            <button
              onClick={handleMarkAllAsRead}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
            >
              Mark all as read
            </button>
          </div>
        )}

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
                <i className="ri-notification-off-line text-3xl text-gray-400"></i>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                No notifications yet
              </h3>
              <p className="text-sm text-gray-500">
                You'll see updates about your projects here
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => {
                const iconConfig = getNotificationIcon(notification.type);
                return (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                      !notification.read ? 'bg-teal-50/30' : ''
                    }`}
                    onClick={() => handleMarkAsRead(notification.id)}
                  >
                    <div className="flex gap-3">
                      <div
                        className={`w-10 h-10 flex items-center justify-center ${iconConfig.bg} rounded-lg shrink-0`}
                      >
                        <i
                          className={`${iconConfig.icon} text-lg ${iconConfig.color}`}
                        ></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p
                            className={`text-sm font-medium ${
                              !notification.read
                                ? 'text-gray-900'
                                : 'text-gray-700'
                            }`}
                          >
                            {notification.message}
                          </p>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-teal-500 rounded-full shrink-0 mt-1"></div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mb-1">
                          {notification.projectName} •{' '}
                          {notification.projectNumber}
                        </p>
                        <p className="text-xs text-gray-400">
                          {getRelativeTime(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
